'use client';

import { useEffect } from 'react';

import { albumApi } from '@/features/design/album/api/album-service';
import { estimateApi } from '@/features/design/estimate/api/estimate-service';
import { rendersService } from '@/shared/api/services/renders.service';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';

import type { Job } from '../model/job-queue.types';
import { useJobQueueStore } from '../model/job-queue.store';
import {
  WATCHDOG_TICK_INTERVAL_MS,
  clearWatchdogProbeFor,
  markWatchdogProbeFired,
  selectJobsToProbe,
} from '../lib/watchdog';

interface UseJobWatchdogOptions {
  isLeader: boolean;
}

/**
 * L4 watchdog (per WEB-L4 in docs/REALTIME_RECONCILE_PLAN.md).
 *
 * Periodic 10s tick on the leader tab. For every active render/upscale/album
 * job whose pending- or processing-deadline has passed AND that wasn't probed
 * in the last 10s, probes the server:
 *
 *   - Render / upscale: `GET /renders/{id}` → `reconcileFromApi([render])`.
 *     The reconcile returns the set of healed conceptIds; the watchdog uses
 *     it to invalidate `cacheKeys.concepts.angles(conceptId)` so the freshly
 *     completed render lands in the carousel without a manual refresh.
 *   - Album: `GET /concepts/{conceptId}/album` →
 *     `reconcileAlbumsFromApi(albums, conceptId)`. Same idea, scoped per
 *     concept.
 *   - 404 → `removeJobById(id)` drops the row (server forgot the job).
 *   - Other errors → log; the re-probe gate absorbs the noise.
 *
 * Follower tabs don't tick — only the leader holds an Ably session and
 * issues server probes. Followers piggyback on BroadcastChannel updates
 * from the leader's reconcile / removeJobById writes.
 */
export function useJobWatchdog({ isLeader }: UseJobWatchdogOptions) {
  useEffect(() => {
    if (!isLeader) return;
    if (typeof window === 'undefined') return;

    const tick = () => {
      runWatchdogTick().catch((error) => {
        wsLogger.error('[L4_WATCHDOG] tick failed:', error);
      });
    };

    const intervalId = window.setInterval(tick, WATCHDOG_TICK_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isLeader]);
}

async function runWatchdogTick(): Promise<void> {
  const now = Date.now();
  const store = useJobQueueStore.getState();
  const candidates = selectJobsToProbe(store.jobQueue, now);
  if (candidates.length === 0) return;

  wsLogger.info(`[L4_WATCHDOG] probing ${candidates.length} overdue job(s)`);

  await Promise.allSettled(candidates.map((job) => probeJob(job)));
}

async function probeJob(job: Job): Promise<void> {
  // Mark BEFORE the await — a slow API call must not trigger a duplicate
  // probe on the next tick.
  markWatchdogProbeFired(job.id);

  if (job.jobType === 'album') {
    await probeAlbumJob(job.id, job.conceptId);
    return;
  }
  if (job.jobType === 'estimate') {
    await probeEstimateJob(job.id);
    return;
  }
  await probeRenderJob(job.id);
}

async function probeRenderJob(jobId: string): Promise<void> {
  try {
    const render = await rendersService.getRender(jobId);
    const healed = useJobQueueStore.getState().reconcileFromApi([render]);
    refreshHealedConcepts(healed);
  } catch (error) {
    if (error instanceof ApiClientError && error.isNotFoundError()) {
      wsLogger.warn(`[L4_WATCHDOG] render job ${jobId} → 404, dropping from queue`);
      useJobQueueStore.getState().removeJobById(jobId);
      clearWatchdogProbeFor(jobId);
      return;
    }
    wsLogger.warn(`[L4_WATCHDOG] render probe ${jobId} failed`, error);
  }
}

async function probeAlbumJob(jobId: string, conceptId: string): Promise<void> {
  try {
    const albums = await albumApi.list(conceptId);
    const found = albums.find((a) => a.id === jobId);
    if (!found) {
      wsLogger.warn(`[L4_WATCHDOG] album ${jobId} not in /album list → dropping`);
      useJobQueueStore.getState().removeJobById(jobId);
      clearWatchdogProbeFor(jobId);
      return;
    }
    useJobQueueStore
      .getState()
      .reconcileAlbumsFromApi(albums, conceptId);
  } catch (error) {
    if (error instanceof ApiClientError && error.isNotFoundError()) {
      wsLogger.warn(`[L4_WATCHDOG] album probe ${jobId} → 404, dropping`);
      useJobQueueStore.getState().removeJobById(jobId);
      clearWatchdogProbeFor(jobId);
      return;
    }
    wsLogger.warn(`[L4_WATCHDOG] album probe ${jobId} failed`, error);
  }
}

async function probeEstimateJob(jobId: string): Promise<void> {
  try {
    const estimate = await estimateApi.get(jobId);
    useJobQueueStore.getState().reconcileEstimateFromApi(estimate);
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.detail(jobId),
    });
  } catch (error) {
    if (error instanceof ApiClientError && error.isNotFoundError()) {
      wsLogger.warn(`[L4_WATCHDOG] estimate probe ${jobId} → 404, dropping`);
      useJobQueueStore.getState().removeJobById(jobId);
      clearWatchdogProbeFor(jobId);
      return;
    }
    wsLogger.warn(`[L4_WATCHDOG] estimate probe ${jobId} failed`, error);
  }
}

function refreshHealedConcepts(conceptIds: Set<string>): void {
  if (conceptIds.size === 0) return;
  for (const conceptId of conceptIds) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.angles(conceptId),
    });
  }
}
